import { useCallback, useEffect, useRef, useState } from "react";
import { styled } from "@pigment-css/react";
import {
  CalendarDays,
  ChevronGrabberVertical,
  ChevronGrabberVerticalThin,
  ChevronRightSmall,
  PencilLine,
} from "@acto/web-common/components";
import { useLocale, useTranslations } from "@acto/web-common/locales";
import type { Customer, User } from "@acto/taskpane/core/domain";
import { AnalyticsEvents, useTrack } from "@acto/taskpane/core/analytics";
import { useCreateTodo } from "@acto/taskpane/core/data-access/todos/useCreateTodo";
import { useViewer } from "@acto/taskpane/core/contexts/ViewerContext";
import { AssigneePickerSheet } from "@acto/taskpane/field-sales-2/components/patterns/AssigneePickerSheet";
import { useAssigneeToggle } from "@acto/taskpane/field-sales-2/data-access/todos/useAssigneeToggle";
import { FullScreenSheet } from "@acto/taskpane/field-sales-2/components/primitives/BottomSheet";
import { useAutoFocusNoScroll } from "@acto/taskpane/field-sales-2/components/primitives/useAutoFocusNoScroll";
import { Divider } from "@acto/taskpane/field-sales-2/components/primitives/Divider";
import { DatePicker } from "@acto/taskpane/field-sales-2/components/primitives/DatePicker";
import { todayUtc, toIsoDate } from "@acto/taskpane/field-sales-2/components/primitives/Calendar";
import { formatDayMonthYear } from "@acto/taskpane/field-sales-2/utils/format";
import { pressInteraction } from "@acto/taskpane/field-sales-2/styles/pressInteraction";
import { ExpandableAccordion } from "@acto/taskpane/field-sales-2/components/patterns/CreateTodoSheet/ui/ExpandableAccordion";
import { CustomerPickerSheet } from "./CustomerPickerSheet";

const Fields = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[16],
  padding: `0 ${theme.fieldSalesTwo.spacing[16]}`,
}));

const Card = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[20],
  padding: `${theme.fieldSalesTwo.spacing[24]} ${theme.fieldSalesTwo.spacing[20]}`,
  borderRadius: theme.fieldSalesTwo.radius[38],
  background: theme.fieldSalesTwo.colors.surface.glass,
}));

// A glass card that delegates spacing to a single full-bleed row/accordion child (matches
// CreateTodoSheet's DueDateCard): the whole surface becomes the tap target.
const PillCard = styled(Card)({
  gap: 0,
  padding: 0,
});

const DescriptionRow = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "flex-start",
  gap: theme.fieldSalesTwo.spacing[8],
  color: theme.fieldSalesTwo.colors.neutral[700],
  "& svg": {
    flexShrink: 0,
    width: theme.fieldSalesTwo.spacing[18],
    height: theme.fieldSalesTwo.spacing[18],
    marginTop: theme.fieldSalesTwo.spacing[2],
  },
}));

const DescriptionInput = styled("textarea")(({ theme }) => ({
  flex: 1,
  minWidth: 0,
  minHeight: "5.5rem",
  resize: "none",
  border: "none",
  background: "transparent",
  outline: "none",
  padding: 0,
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size.input,
  fontWeight: theme.fieldSalesTwo.font.weight[500],
  lineHeight: theme.fieldSalesTwo.font.lineHeight[148],
  color: theme.fieldSalesTwo.colors.neutral[900],
  "&::placeholder": { color: theme.fieldSalesTwo.colors.neutral[700] },
}));

// Shared pill layout for the Kunde selector and the due-date toggle.
const SelectRow = styled("button")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: theme.fieldSalesTwo.spacing[16],
  width: "100%",
  border: "none",
  padding: `${theme.fieldSalesTwo.spacing[24]} ${theme.fieldSalesTwo.spacing[20]}`,
  cursor: "pointer",
  WebkitTapHighlightColor: "transparent",
  borderRadius: theme.fieldSalesTwo.radius[38],
  background: "transparent",
  transition: "background 120ms ease",
  "&:hover": { background: theme.fieldSalesTwo.colors.surface.glassMedium },
  "&:active": { background: theme.fieldSalesTwo.colors.surface.glassStrong },
  ...pressInteraction(theme, { scale: 1 }),
  // Expanded, the calendar sits above this toggle inside the same card, so square off the top.
  '&[aria-expanded="true"]': {
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
  },
  // Disabled means "not clickable" — the chevron is dropped at the call site, but the label stays
  // full-contrast rather than greying out.
  "&:disabled": { pointerEvents: "none", cursor: "default" },
}));

const SelectLeft = styled("span")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.fieldSalesTwo.spacing[8],
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size.base,
  fontWeight: theme.fieldSalesTwo.font.weight[500],
  color: theme.fieldSalesTwo.colors.neutral[700],
  "& svg": { width: theme.fieldSalesTwo.spacing[20], height: theme.fieldSalesTwo.spacing[20] },
}));

const SelectValue = styled("span")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.fieldSalesTwo.spacing[8],
  minWidth: 0,
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size.base,
  fontWeight: theme.fieldSalesTwo.font.weight[520],
  color: theme.fieldSalesTwo.colors.neutral[900],
  '&[data-placeholder="true"]': {
    color: theme.fieldSalesTwo.colors.neutral[700],
    fontWeight: theme.fieldSalesTwo.font.weight[500],
  },
  "& svg": { width: theme.fieldSalesTwo.spacing[18], height: theme.fieldSalesTwo.spacing[18], flexShrink: 0 },
}));

const SelectValueText = styled("span")({
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
});

// Sticky bottom CTA, matching CreateTodoSheet.
const CtaRow = styled("div")(({ theme }) => ({
  position: "sticky",
  bottom: 0,
  zIndex: 2,
  marginTop: "auto",
  display: "flex",
  justifyContent: "center",
  padding: `${theme.fieldSalesTwo.spacing[24]} ${theme.fieldSalesTwo.spacing[16]} ${theme.fieldSalesTwo.spacing[32]}`,
  background: `linear-gradient(to top, ${theme.fieldSalesTwo.colors.background} 26.5%, rgba(240,238,237,0))`,
  pointerEvents: "none",
  "& > *": { pointerEvents: "auto" },
}));

const SubmitButton = styled("button")(({ theme }) => ({
  width: "100%",
  minHeight: "3.5rem",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  boxSizing: "border-box",
  paddingInline: theme.fieldSalesTwo.spacing[20],
  border: `${theme.strokeWidth.thin} solid ${theme.fieldSalesTwo.colors.surface.glassBorder}`,
  borderRadius: theme.fieldSalesTwo.radius[38],
  background: theme.fieldSalesTwo.colors.accent.action,
  color: theme.fieldSalesTwo.colors.neutral[50],
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[15],
  fontWeight: theme.fieldSalesTwo.font.weight[520],
  letterSpacing: theme.fieldSalesTwo.font.letterSpacing.subtle,
  cursor: "pointer",
  WebkitTapHighlightColor: "transparent",
  transition: "filter 120ms ease, transform 120ms ease",
  ...pressInteraction(theme),
  "&:hover": { filter: "brightness(0.93)" },
  "&:disabled": { opacity: 0.5, pointerEvents: "none" },
}));

// Weekday-only picker default: today, or the next Monday on a weekend.
const defaultDueDate = (): Date => {
  const today = todayUtc();
  const weekday = today.getUTCDay();
  const offset = weekday === 6 ? 2 : weekday === 0 ? 1 : 0;
  return new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate() + offset));
};

interface CreateTodoOverlayProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  /** Fired after a todo is successfully created (overlay already closing) — e.g. to show a toast. */
  onCreated: () => void;
}

export function CreateTodoOverlay({ isOpen, onOpenChange, onCreated }: CreateTodoOverlayProps) {
  const t = useTranslations("fieldSalesTwo.todoCreate");
  const { locale } = useLocale();
  const bodyRef = useRef<HTMLDivElement>(null);
  const { createTodo, result } = useCreateTodo();
  const track = useTrack();
  const { viewer } = useViewer();

  const descriptionRef = useAutoFocusNoScroll<HTMLTextAreaElement>();
  const [description, setDescription] = useState("");
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [dueDate, setDueDate] = useState<Date>(defaultDueDate);
  const [calendarExpanded, setCalendarExpanded] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  // null = no pick; the server then assigns the creating viewer.
  const [assignee, setAssignee] = useState<User | null>(null);
  const [assigneePickerOpen, setAssigneePickerOpen] = useState(false);
  const assigneeRowRef = useRef<HTMLButtonElement>(null);

  const { disabled: assigneeDisabled } = useAssigneeToggle(customer?.id, assignee);

  const dueDateLabel = formatDayMonthYear(dueDate, locale);
  const canSubmit = description.trim().length > 0 && customer !== null && !result.fetching;

  const resetForm = useCallback(() => {
    setDescription("");
    setCustomer(null);
    setDueDate(defaultDueDate());
    setCalendarExpanded(false);
    setPickerOpen(false);
    setAssignee(null);
    setAssigneePickerOpen(false);
  }, []);

  // Closing without saving (header X, Escape, backdrop) must discard the draft so reopening starts
  // fresh. The form state lives on this outer component, which FullScreenSheet's Radix portal does
  // NOT unmount on close (unlike CreateTodoSheet's inner-child state), so reset explicitly here.
  useEffect(() => {
    if (!isOpen) resetForm();
  }, [isOpen, resetForm]);

  const handleCreate = async () => {
    if (!canSubmit || !customer) return;
    const created = await createTodo({
      id: crypto.randomUUID(),
      description: description.trim(),
      dueDate: toIsoDate(dueDate),
      clientId: customer.id,
      assignedUserId: assignee?.id,
    });
    if (created) {
      track(AnalyticsEvents.TODO_CREATED, {
        todoId: created.id,
        customerId: customer.id,
        customerName: customer.name,
        source: "todos_overview",
        hasDueDate: Boolean(dueDate),
      });
      onOpenChange(false);
      onCreated();
    }
  };

  return (
    <>
      <FullScreenSheet
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        title={t("newTaskTitle")}
        closeAriaLabel={t("closeAriaLabel")}
        bodyRef={bodyRef}
      >
        <Fields>
          <Card>
            <DescriptionRow>
              <PencilLine aria-hidden="true" />
              <DescriptionInput
                ref={descriptionRef}
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                placeholder={t("descriptionPlaceholder")}
                aria-label={t("descriptionPlaceholder")}
              />
            </DescriptionRow>
          </Card>

          <PillCard>
            <SelectRow type="button" onClick={() => setPickerOpen(true)}>
              <SelectLeft>{t("customerLabel")}</SelectLeft>
              <SelectValue data-placeholder={customer === null}>
                <SelectValueText>{customer ? customer.name : t("customerPlaceholder")}</SelectValueText>
                <ChevronRightSmall aria-hidden="true" />
              </SelectValue>
            </SelectRow>
          </PillCard>

          <PillCard>
            <SelectRow
              ref={assigneeRowRef}
              type="button"
              onClick={() => setAssigneePickerOpen(true)}
              disabled={assigneeDisabled}
            >
              <SelectLeft>{t("assigneeLabel")}</SelectLeft>
              <SelectValue data-placeholder={!assignee && !viewer?.displayName}>
                <SelectValueText>
                  {assignee?.displayName ?? viewer?.displayName ?? t("assigneePlaceholder")}
                </SelectValueText>
                {!assigneeDisabled && <ChevronGrabberVerticalThin aria-hidden="true" />}
              </SelectValue>
            </SelectRow>
          </PillCard>

          <PillCard>
            <ExpandableAccordion calendarExpanded={calendarExpanded} scrollRef={bodyRef}>
              <DatePicker
                value={dueDate}
                includeWeekends={false}
                onChange={(date) => {
                  setDueDate(date);
                  setCalendarExpanded(false);
                }}
              />
              <Divider />
            </ExpandableAccordion>
            <SelectRow
              type="button"
              aria-expanded={calendarExpanded}
              onClick={() => setCalendarExpanded((expanded) => !expanded)}
            >
              <SelectLeft>
                <CalendarDays aria-hidden="true" />
                {t("dueDateLabel")}
              </SelectLeft>
              <SelectValue>
                <SelectValueText>{dueDateLabel}</SelectValueText>
                <ChevronGrabberVertical aria-hidden="true" />
              </SelectValue>
            </SelectRow>
          </PillCard>
        </Fields>

        <CtaRow>
          <SubmitButton type="button" onClick={handleCreate} disabled={!canSubmit}>
            {t("submit")}
          </SubmitButton>
        </CtaRow>

        {/* Must stay inside the sheet. The popover portals out of the DOM, so Radix only counts a
            tap on it as "inside" the dialog when the event passes through the dialog's React
            subtree. Rendered as a sibling, picking an assignee dismisses the whole sheet. */}
        <AssigneePickerSheet
          isOpen={assigneePickerOpen}
          onOpenChange={setAssigneePickerOpen}
          anchorRef={assigneeRowRef}
          clientId={customer?.id}
          selectedId={assignee?.id}
          onSelect={setAssignee}
        />
      </FullScreenSheet>

      <CustomerPickerSheet
        isOpen={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={(selected) => {
          setCustomer(selected);
          // The assignable list is client-scoped, so a customer change invalidates any prior pick.
          setAssignee(null);
          setPickerOpen(false);
        }}
      />
    </>
  );
}
